// ── Heat Distortion Effect ──
// Custom pmndrs/postprocessing Effect for heat shimmer above fire/molotov zones.
// Displaces UVs near fire sources to create a rising heat haze.

import { Effect, BlendFunction } from 'postprocessing';
import * as THREE from 'three';

const fragmentShader = /* glsl */ `
uniform float time;
uniform vec4 fireSources[8]; // xy = screen pos, z = intensity, w = radius
uniform int sourceCount;

void mainUv(inout vec2 uv) {
  for (int i = 0; i < 8; i++) {
    if (i >= sourceCount) break;
    vec4 src = fireSources[i];
    vec2 delta = uv - src.xy;
    float dist = length(delta);
    if (dist < src.w && uv.y > src.y) { // only above fire
      float falloff = 1.0 - dist / src.w;
      float distort = falloff * src.z * 0.003;
      uv.x += sin(uv.y * 80.0 + time * 3.0) * distort;
      uv.x += sin(uv.y * 120.0 + time * 5.0) * distort * 0.5;
      uv.y += cos(uv.x * 60.0 + time * 4.0) * distort * 0.3;
    }
  }
}`;

/**
 * HeatDistortionEffect — screen-space UV distortion above fire sources.
 *
 * Usage:
 *   const effect = new HeatDistortionEffect();
 *   // Add to EffectPass in postprocessing pipeline
 *   // Each frame, update fire source positions in screen space:
 *   effect.setFireSource(0, screenX, screenY, intensity, radius);
 *   effect.clearSources(); // when fires expire
 */
export class HeatDistortionEffect extends Effect {
  private fireSources: THREE.Vector4[];
  private sourceCount: THREE.Uniform<number>;

  constructor() {
    const fireSources = Array.from({ length: 8 }, () => new THREE.Vector4());
    const sourceCount = new THREE.Uniform(0);

    super('HeatDistortion', fragmentShader, {
      blendFunction: BlendFunction.NORMAL,
      uniforms: new Map<string, THREE.Uniform>([
        ['time', new THREE.Uniform(0)],
        ['fireSources', new THREE.Uniform(fireSources)],
        ['sourceCount', sourceCount],
      ]),
    });

    this.fireSources = fireSources;
    this.sourceCount = sourceCount;
  }

  /**
   * Set a fire source position in normalized screen coordinates.
   * @param index - Slot index (0-7, max 8 sources)
   * @param screenX - Normalized screen X (0-1)
   * @param screenY - Normalized screen Y (0-1, 0=bottom in UV space)
   * @param intensity - Distortion strength (0-1 typical)
   * @param radius - Radius of influence in UV space
   */
  setFireSource(index: number, screenX: number, screenY: number, intensity: number, radius: number): void {
    if (index < 0 || index >= 8) return;
    this.fireSources[index].set(screenX, screenY, intensity, radius);
    this.sourceCount.value = Math.max(this.sourceCount.value, index + 1);
  }

  /**
   * Clear all fire sources (e.g., when all fires expire).
   */
  clearSources(): void {
    this.sourceCount.value = 0;
  }

  /**
   * Called each frame by the postprocessing pipeline.
   * Advances the internal time uniform for animated distortion.
   */
  update(
    _renderer: THREE.WebGLRenderer,
    _inputBuffer: THREE.WebGLRenderTarget,
    deltaTime?: number,
  ): void {
    const timeUniform = this.uniforms.get('time');
    if (timeUniform) {
      (timeUniform.value as number) += deltaTime ?? 0;
    }
  }
}
